import React, { createContext, useContext, useReducer, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from './user-context';
import { NotificationHistoryLogItem, NotificationItem, NotificationState, NotificationTemplate } from '../../interfaces/common/notifications';
import { API_ENDPOINT } from '../../configurations/global.config';

interface NotificationContextType extends NotificationState {
  addNotification: (notification: Omit<NotificationItem, 'timestamp' | 'isRead'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  dismissNotification: (id: string) => void;
  dismissAllNotifications: () => void;
  clearNotifications: () => void;
  reloadNotifications: () => void;
  getLocalizedNotifications: () => Array<NotificationItem & { localizedTitle: string; localizedBody: string }>;
}

const initialState: NotificationState = {
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  lastSeenLogId: 0,
};

type NotificationAction =
  | { type: 'ADD_NOTIFICATION'; payload: NotificationItem }
  | { type: 'MARK_AS_READ'; payload: string }
  | { type: 'MAKR_ALL_AS_READ' }
  | { type: 'DISMISS_NOTIFICATION'; payload: string }
  | { type: 'DISMISS_ALL_NOTIFICATIONS' }
  | { type: 'CLEAR_NOTIFICATIONS' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_NOTIFICATIONS'; payload: NotificationItem[] }
  | { type: 'SET_LAST_SEEN_LOG_ID'; payload: number }

function notificationReducer(state: NotificationState, action: NotificationAction): NotificationState {
  switch (action.type) {
    case 'ADD_NOTIFICATION': {
        console.log(action.payload)
        const notificationLogId = action.payload.notificationLogId
        const notificationId = action.payload.notificationId
        const itemId = action.payload.id

        if (state.notifications.find(item => item.notificationId == notificationId || item.notificationLogId == notificationLogId || item.id == itemId))
            return state
        else {
            return {
            ...state,
            notifications: [action.payload, ...state.notifications],
            unreadCount: state.unreadCount + 1,
            lastSeenLogId: Math.max(action.payload.notificationLogId, state.lastSeenLogId)
        }}
    }

    case 'MARK_AS_READ':
      return {
        ...state,
        notifications: state.notifications.map(n =>
          n.id === action.payload ? { ...n, isRead: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      };
    case 'MAKR_ALL_AS_READ':
      return {
        ...state,
        notifications: state.notifications.map(n => {return {...n, isRead: true}}),
        unreadCount: 0,
      };
    case 'DISMISS_NOTIFICATION':
      return {
        ...state,
        notifications: state.notifications.filter(n => n.id !== action.payload),
        unreadCount: state.notifications.find(n => n.id === action.payload)?.isRead === false
          ? Math.max(0, state.unreadCount - 1)
          : state.unreadCount,
      };
    case 'DISMISS_ALL_NOTIFICATIONS':
    case 'CLEAR_NOTIFICATIONS':
      return { ...state, notifications: [], unreadCount: 0 };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_NOTIFICATIONS':
      return {
        ...state,
        notifications: action.payload,
        unreadCount: action.payload.filter(n => !n.isRead).length,
      };
    case 'SET_LAST_SEEN_LOG_ID':
        return {
            ...state,
            lastSeenLogId: action.payload
        }
    default:
      return state;
  }
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, config } = useContext(AuthContext);
  const [state, dispatch] = useReducer(notificationReducer, initialState);

  // Load from localStorage on mount
  useEffect(() => {
    if (!user) return;
    const storedNotifications = localStorage.getItem('@notifications');
    const storedLastSeenLogId = localStorage.getItem('@notifications_last_seen_logid');
    if (storedNotifications) {
      const parsed: NotificationItem[] = JSON.parse(storedNotifications);
      dispatch({ type: 'SET_NOTIFICATIONS', payload: parsed });
    }

    if (storedLastSeenLogId) {
        const parsed: number = JSON.parse(storedLastSeenLogId);
        if (isNaN(parsed)) {
            return;
        }
        dispatch({ type: 'SET_LAST_SEEN_LOG_ID', payload: parsed });
    }
  }, [user]);

  // Persist to localStorage whenever notifications change
  useEffect(() => {
    localStorage.setItem('@notifications', JSON.stringify(state.notifications));
  }, [state.notifications]);

  useEffect(()=>{
    localStorage.setItem('@notifications_last_seen_logid', JSON.stringify(state.lastSeenLogId))
  }, [state.lastSeenLogId])

  console.log("state from render: ", state)

  // Add a new notification
  const addNotification = useCallback((notification: Omit<NotificationItem, 'timestamp' | 'isRead'>) => {
    if (
      (notification.notificationLogId && state.notifications.some(n => n.notificationLogId === notification.notificationLogId)) ||
      (notification.notificationId && state.notifications.some(n => n.notificationId === notification.notificationId))
    ) return;

    if (!notification.notificationLogId) {
        console.log("no log id, skipping ....")
        return;
    }

    const id = notification.notificationLogId.toString()
    if (!id) {
        console.log("no id, skipping...");
        return;
    }

    const newNotification: NotificationItem = {
      ...notification,
      id: id,
      timestamp: Date.now(),
      isRead: false,
    };

    dispatch({ type: 'ADD_NOTIFICATION', payload: newNotification });
  }, []);

  const markAsRead = useCallback((id: string) => dispatch({ type: 'MARK_AS_READ', payload: id }), []);

  const markAllAsRead = useCallback(async () => {
    if (!user) return;
    const lastNotification = state.notifications[0];
    if (lastNotification) {
      try {
        await axios.post(
          `${API_ENDPOINT}/v1/notifications/last-seen/`,
          {
            device_token: localStorage.getItem('fcm_token'),
            notification_log_id: state.lastSeenLogId,
          },
          config
        );
      } catch (err) {
        console.error('Failed to mark all as read on backend', err);
      }
    }
    dispatch({ type: 'MAKR_ALL_AS_READ' });
  }, [state.notifications, user, config]);

  const dismissNotification = useCallback((id: string) => dispatch({ type: 'DISMISS_NOTIFICATION', payload: id }), []);
  const dismissAllNotifications = useCallback(() => dispatch({ type: 'DISMISS_ALL_NOTIFICATIONS' }), []);
  const clearNotifications = useCallback(() => dispatch({ type: 'CLEAR_NOTIFICATIONS' }), []);
  const reloadNotifications = useCallback(() => {
    const stored = localStorage.getItem('@notifications');
    if (stored) {
      dispatch({ type: 'SET_NOTIFICATIONS', payload: JSON.parse(stored) });
    }
  }, []);

  const fetchHistory = async () => {
    try {
      const lastId = state.lastSeenLogId

      if (!lastId) return

      console.log("Fetching history with last id = ", lastId)

      const response = await axios.get<{count: number, results: NotificationHistoryLogItem[]}>(`${API_ENDPOINT}/v1/notifications/history/?since_id=${lastId}&device_token=${localStorage.getItem("fcm_token")}`, config);
      const newNotifications: (Omit<NotificationItem ,'timestamp' | 'isRead'> | null)[] = response.data.results.map(historyLogItem => {
          const logId = parseInt(historyLogItem.data.notification_log_id, 10);
          if (Number.isNaN(logId)) {
              console.error('Failed to parse notification_log_id:', historyLogItem.data.notification_log_id);
              return null
          }
          return {
              id: logId.toString(), // use string to match mobile, even if backend returns number
              title: historyLogItem.data.title,
              body: historyLogItem.data.body,
              data: historyLogItem.data,
              notificationId: historyLogItem.data.notification_id, // UUID from sender to prevent duplicates
              notificationLogId: logId, // Backend notification log ID for syncing
              templateName: historyLogItem.template_name as NotificationTemplate, // Notification template name
          }
      }).filter(item => item != null);

      console.log("Got from history: ", response)

      newNotifications.filter(n => n!=null).forEach((n) => addNotification(n!));
    } catch (err) {
      console.error('Failed to fetch notification history', err);
    }
  };


  const getLocalizedNotifications = useCallback(() => {
    return state.notifications.map(n => ({
      ...n,
      localizedTitle: n.title,
      localizedBody: n.body,
    }));
  }, [state.notifications]);

  useEffect(() => {
    console.log("State changed: ", state)
  }, [state])

  /** Fetch notification history whenever app becomes visible */
  useEffect(() => {
    if (!user) return;
    // Fetch on mount
    fetchHistory();

    // Fetch when app becomes visible
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchHistory();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, config, addNotification, state.lastSeenLogId]);

  return (
    <NotificationContext.Provider
      value={{
        ...state,
        addNotification,
        markAsRead,
        markAllAsRead,
        dismissNotification,
        dismissAllNotifications,
        clearNotifications,
        reloadNotifications,
        getLocalizedNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) throw new Error('useNotifications must be used within NotificationProvider');
  return context;
};
